import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/** The raised light control: white face, hairline border, inset bottom edge. */
const RAISED = 'border border-border bg-background text-foreground shadow-inner-sm hover:bg-muted';

export const buttonVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 ease-out active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:opacity-90',
        secondary: RAISED,
        outline: RAISED,
        ghost: 'bg-transparent text-foreground hover:bg-muted',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        link: 'bg-transparent text-brand underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        sm: 'h-7 px-2.5',
        default: 'h-8 px-3',
        lg: 'h-9 px-4',
        icon: 'size-8',
        'icon-sm': 'size-7',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

type ButtonBaseProps = Omit<React.ComponentProps<'button'>, 'aria-label'> &
  Omit<VariantProps<typeof buttonVariants>, 'size'> & {
    asChild?: boolean;
  };

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

/**
 * An icon-only button has no accessible name of its own, so the type system
 * makes `aria-label` mandatory the moment an icon size is chosen.
 */
export type ButtonProps = ButtonBaseProps &
  (
    | { size?: Exclude<ButtonSize, 'icon' | 'icon-sm'>; 'aria-label'?: string }
    | { size: 'icon' | 'icon-sm'; 'aria-label': string }
  );

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
